import {
  useIsGuestOfAssetWorkspace,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import type { ColumnData, SlideLayoutLocationState } from '@gripp/shared-ui';
import { SlideAction, SlideLayout, ViewMode } from '@gripp/shared-ui';
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AssetRouteProvider } from '../assetRouteContext';
import { RoutineAllColumn } from './asset-routines-all-column';
import { AssetRoutinesHeader } from './asset-routines-header';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

const ASSET_ROUTINES_ALL_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/asset-routines-all.tsx';
const ASSET_CARD_COMPONENT_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetCard.tsx';
const ACTIVITY_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/activity/routes.tsx';
const ASSET_SUB_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/assetSubRoutes.tsx';
const ACTIVITY_ASSET_ROUTINES_ALL_ROUTE_PATTERN =
  '/activities/:issueId/asset/:assetId/routines/all';
const ASSET_ROUTINES_ALL_ROUTE_PATTERN = '/assets/:assetId/routines/all';

export const AssetRoutinesAll = () => {
  const { assetId = '', issueId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [count, setCount] = useState<number>();
  const { workspaceAsset } = useWorkspaceAsset(assetId, !assetId);
  const assetWorkspaceId = workspaceAsset?.workspace?.id;
  // View + submit are on the guest allowlist for this route (assetSubRoutes.tsx
  // leaves it unwrapped), but the asset-edit gear (AssetFilters) and the
  // assign/rearrange gear (AssetRoutinesHeader, which navigates to the
  // still-gated routines/edit route) are not -- withhold both here rather than
  // let a guest hit a FORBIDDEN dead end.
  const isGuestOfAssetWorkspace = useIsGuestOfAssetWorkspace(assetWorkspaceId);
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  if (!assetId) {
    return null;
  }

  const leftColumn: ColumnData = {
    title: 'assets.menu.title',
    mainComponent: (
      <AssetRouteProvider
        value={{
          basePath: issueId
            ? `/activities/${issueId}/asset/${assetId}`
            : `/assets/${assetId}`,
          listPath: issueId ? '/activities' : '/assets',
          isWorkspaceMode: false,
          issueId,
        }}
      >
        <AssetCard assetId={assetId} />
      </AssetRouteProvider>
    ),
    headerComponent: (
      <AssetFilters
        assetId={assetId}
        showEditButton={!isGuestOfAssetWorkspace}
      />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: () =>
      navigate(issueId ? '../../../..' : '../..', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      }),
    disableScroll: true,
    debug: {
      id: issueId
        ? 'activity-conversation:third-asset-card'
        : 'asset-routines-all:left-asset-card',
      titleKey: issueId ? 'assets.menu.titleSingular' : 'assets.menu.title',
      component: 'AssetCard',
      componentSource: ASSET_CARD_COMPONENT_SOURCE,
      columnSource: ASSET_ROUTINES_ALL_SOURCE,
      ownerComponent: 'AssetRoutinesAll',
      ownerSource: ASSET_ROUTINES_ALL_SOURCE,
      routePanelKey: issueId ? 'activity-asset-card' : 'asset-routines-all',
      routePattern: issueId
        ? ACTIVITY_ASSET_ROUTINES_ALL_ROUTE_PATTERN
        : ASSET_ROUTINES_ALL_ROUTE_PATTERN,
      routeSource: issueId ? ACTIVITY_ROUTES_SOURCE : ASSET_SUB_ROUTES_SOURCE,
      source: {
        owner: {
          file: ASSET_ROUTINES_ALL_SOURCE,
          symbol: 'AssetRoutinesAll',
        },
        column: {
          file: ASSET_ROUTINES_ALL_SOURCE,
          symbol: 'leftColumn',
        },
        component: {
          file: ASSET_CARD_COMPONENT_SOURCE,
          symbol: 'AssetCard',
        },
        route: {
          file: issueId ? ACTIVITY_ROUTES_SOURCE : ASSET_SUB_ROUTES_SOURCE,
          symbol: issueId ? 'activityRoutes' : 'assetSubRoutes',
        },
      },
      entity: { type: 'asset', id: assetId },
    },
  };

  const rightColumn: ColumnData = {
    title: 'assets.routines',
    count,
    mainComponent: (
      <RoutineAllColumn assetId={assetId} onItemsCountChange={setCount} />
    ),
    showBackButton: true,
    // AssetRoutinesHeader self-gates for a guest (assign/rearrange is off
    // the allowlist, view + submit stays on it).
    headerComponent: (
      <AssetRoutinesHeader assetId={assetId} issueId={issueId} />
    ),
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    onBackButtonPress: () =>
      navigate('../..', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      }),
    disableScroll: true,
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};
